import * as yup from 'yup';
import { Fragment, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-form-state-manager';

import { GetProductQuery as GetProduct, ProductType, SaveProductPricingInput } from '__generated__/graphql';

import { SinglePromotionForm } from '../../../Common/PricingForm';
import { dateRangeObject } from '../../../Common/DateRangeForm';
import { getServerErrors } from '../../../../common/helpers';
import { translationsForAttribute } from '../../../Translations/helpers';
import UI from '../../../../common/UI';
import useEditProductMutation from './useEditProductMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useNumberInput from '../../../../common/useNumberInput';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

type Event = GetProduct['event'];
type Product = GetProduct['product'];

const getEditProductPricingSchema = () => yup.object().shape({
  vat_percentage: yup.number().min(0).max(99.999).nullable(),
  promotions: yup.array(yup.object().shape({
    title: yup.string().nullable().test('required', '', (value, { path }) => (
      path === 'promotions[0].title' ? true : !!value
    )),
    capacity: yup.number().nullable(),
    description: yup.string().ensure(),
    amount: yup.number().test('required', '', (value) => value >= 0),
    restricted_to_tickets: yup.array(yup.object().shape({
      id: yup.string(),
    })),
    restricted_to_promotions: yup.array(yup.object().shape({
      id: yup.string(),
    })),
    ...dateRangeObject(),
  })),
});

const getValues = (product: Product) => ({
  promotions: [
    ...product.promotions.map((promotion) => ({
      id: promotion.id,
      title: promotion.title,
      title_translations: translationsForAttribute(promotion.translations, 'title'),
      capacity: promotion.capacity,
      amount: promotion.amount,
      start_date: promotion.start_date,
      end_date: promotion.end_date,
      active: promotion.active,
      restricted_to_tickets: promotion.restricted_to_tickets.map(({ id }) => ({ id })),
      restricted_to_promotions: promotion.restricted_to_promotions.map(({ id }) => ({ id })),
    })),
  ],
  products: product.products.map(({ id }) => id),
  vat_percentage: product.vat_percentage > 0 ? product.vat_percentage : null,
  hide_on_invoice: product.hide_on_invoice,
  ledger_account: product.ledger_account,
});

export interface EditProductPricingFormProps {
  product: Product;
  event: Event;
}

const EditProductPricingFeeForm = ({ product, event }: EditProductPricingFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const numberInput = useNumberInput();

  const hasVatNumber = Boolean(event.vat_number || project.organisation_vat_number);

  const form = useForm<SaveProductPricingInput>({
    values: getValues(product),
  });

  useNavigationPrompt(form.changed());

  const getPromotionData = () => (
    form.values.promotions.length === 1
      // Since the promotion capacity field disappears when there is only 1 promotion,
      // make sure we unset it on save.
      ? [{ ...form.values.promotions[0], capacity: null, title: null, active: true }]
      : form.values.promotions
  );

  const formValues = {
    ...form.values,
    promotions: getPromotionData(),
  };

  const [editProduct, { error, loading }] = useEditProductMutation({
    input: {
      id: product.id,
      pricing: formValues,
    },
    onSuccess: (product) => {
      form.reset(getValues(product));
    },
  });

  const editProductPricingSchema = useMemo(
    () => getEditProductPricingSchema(),
    [],
  );

  const { valid, errors } = useValidation({
    schema: editProductPricingSchema,
    values: formValues,
    externalErrors: getServerErrors(error),
  });

  const availableWithProducts = event.products.filter((product) => product.type !== ProductType.fee);

  return (
    <UI.Form onSubmit={() => editProduct()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ block: true, mb: 1 }}>
            {t('pricing')}
          </UI.Legend>
          <UI.InputDescription>
            {t('product_fee_pricing_description')}
          </UI.InputDescription>
        </UI.Div>

        <SinglePromotionForm
          form={form}
          event={event}
          errors={errors}
        />

        {product.stand_alone && availableWithProducts.length > 0 && (
          <UI.InputGroup>
            <UI.InputLabel>
              {t('available_with_products')}
            </UI.InputLabel>
            <UI.Checkbox
              checked={form.values.products.length === 0}
              disabled={form.values.products.length === 0}
              onChange={() => form.set('products', [])}
            >
              {t('all_products')}
            </UI.Checkbox>
            {availableWithProducts.map((product) => (
              <Fragment key={product.id}>
                <UI.Checkbox
                  {...form.checklist('products', product.id)}
                  key={product.id}
                >
                  {product.title}
                </UI.Checkbox>
              </Fragment>
            ))}
            <UI.ErrorMessages attribute={t('products')} errors={errors.products} />
          </UI.InputGroup>
        )}

        <UI.HR />

        <UI.Div>
          <UI.InputGroup>
            <UI.InputLabel>{t('vat_rate')}</UI.InputLabel>
            <UI.InputDescription>
              {hasVatNumber && t('vat_description')}
              {!hasVatNumber && (
                <Trans i18nKey="charging_vat_description">
                  <UI.Link to={`/${project.id}/events/${event.id}/settings/payments`} />
                </Trans>
              )}
            </UI.InputDescription>
            {hasVatNumber && (
              <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
                <UI.InputWrapper>
                  <UI.Input
                    {...form.text(
                      'vat_percentage',
                      { inputHandler: numberInput({ min: 0, max: 99.999, decimals: 3 }) },
                    )}
                    sc={{ pr: 4 }}
                  />
                  <span>%</span>
                </UI.InputWrapper>
                <UI.ErrorMessages attribute={t('vat_rate')} errors={errors.vat_percentage} />
              </UI.FormGrid>
            )}
          </UI.InputGroup>
        </UI.Div>

        <UI.InputGroup sc={{ valid: !errors.hide_on_invoice, touched: form.touched.hide_on_invoice }}>
          <UI.InputLabel>{t('hide_on_invoice')}</UI.InputLabel>
          <UI.InputDescription>
            {t('hide_on_invoice_product_description')}
          </UI.InputDescription>
          <UI.Checkbox {...form.checkbox('hide_on_invoice')}>
            {t('hide_on_invoice')}
          </UI.Checkbox>
          <UI.ErrorMessages attribute={t('hide_on_invoice')} errors={errors.hide_on_invoice} />
        </UI.InputGroup>

        <UI.HR />

        <UI.Div>
          <UI.Legend sc={{ block: true, mb: 1 }}>
            {t('ledger_account')}
          </UI.Legend>
          <UI.InputDescription>
            {t('ledger_account_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
          <UI.InputGroup sc={{ valid: !errors.ledger_account, touched: form.touched.ledger_account }}>
            <UI.InputLabel htmlFor="LedgerAccount">
              {t('ledger_account')}
            </UI.InputLabel>
            <UI.DebouncedInput {...form.text('ledger_account')} id="LedgerAccount" />
            <UI.ErrorMessages attribute={t('ledger_account')} errors={errors.title} />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_product')}
          closeTo={`/${project.id}/events/${event.id}/products`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditProductPricingFeeForm;
